import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, NO_ERRORS_SCHEMA, OnInit, signal, input } from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import _ from 'lodash';
import { catchError, EMPTY, from, map, Observable, of, takeUntil, tap } from 'rxjs';
import { Notifications } from '../../../../../../angularJs/src/app/services/notification.service';
import { ROLES, USER_TITLES } from '../../../../../../angularJs/src/app/services/user.service';
import Utils from '../../../../../../angularJs/src/app/services/utils.service';
import { BusinessService } from '../../../../api/business/business.service';
import { StaffUsersService } from '../../../../api/staff-users/staff-users.service';
import { Unsubscribe } from '../../../../core/unsubscribe/unsubscribe';
import { PrescriberFilesComponent } from './prescriber-files/prescriber-files.component';

interface Role {
  name: string;
  checked: boolean;
}

interface State {
  id: number;
  code: string;
  supported: number;
  slv: number;
  slv_enabled: number;
  state: string;
}

interface License {
  id: number;
  state_code: string;
  license_number: number | string;
}

interface StaffUser {
  id: number;
  title: string;
  firstname: string;
  lastname: string;
  initials: string;
  credentials: string;
  mfa_bypass: boolean;
  active: boolean;
  roles: string[];
  licenses?: License[];
}

const defaultStaffUser: StaffUser = {
  id: 0,
  title: '',
  firstname: '',
  lastname: '',
  initials: '',
  mfa_bypass: false,
  credentials: '',
  active: false,
  roles: [],
};

const defaultState: State = {
  id: 0,
  code: 'SELECT',
  supported: 1,
  slv: 1,
  slv_enabled: 1,
  state: 'Select a state',
};

@Component({
  selector: 'staff-user',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatCard,
    MatCardContent,
    MatSlideToggle,
    PrescriberFilesComponent,
  ],
  templateUrl: './staff-user.component.html',
  styleUrl: './staff-user.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class StaffUserComponent extends Unsubscribe implements OnInit {
  private env = inject<PIMSEnvironment>(ENVIRONMENT);
  private snackBar = inject(SnackNotificationsDialogService);
  private Notifications = inject(Notifications);
  private fb = inject(FormBuilder);
  private formBuilder = inject(FormBuilder);
  private Utils = inject(Utils);
  private Business = inject(BusinessService);
  private staffUserApiService = inject(StaffUsersService);
  readonly userId = input<any>();

  userForm: FormGroup;
  rolesForm: FormGroup;
  licenseForm: FormGroup;
  selectedState = '';
  roles: Role[] = [];
  states: State[] = [defaultState];
  states$: Observable<State[]> = from([]);
  roles$: Observable<any[]> = from([]);
  user$: Observable<StaffUser> = of(defaultStaffUser);
  dataSource = [];
  notes: string | null = '';
  user: StaffUser = defaultStaffUser;
  licenseNumber: string | number = '';
  userTitles: string[] = USER_TITLES;
  displayedColumns: string[] = ['id', 'state_code', 'license_number', 'buttons'];
  informationButtonHovered = false;
  rolesButtonHovered = false;
  licenseButtonHovered = false;
  isUserValidForSignatureUpload$!: Observable<boolean>;
  MFA_STATES = signal([
    { id: 'DISABLED', label: 'MFA Disabled', value: 0 }, // Turning OFF MFA when global MFA is turned ON
    { id: 'ENABLED', label: 'MFA Enabled', value: 1 }, // MFA enabled when global MFA is turned ON for everyone, but OFF for you
    { id: 'FORCED', label: 'MFA Forced', value: 2 }, // Force MFA ON when MFA is OFF for Everyone
  ]);

  constructor() {
    super();
    this.userForm = this.formBuilder.group({
      title: ['', Validators.required],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      mfa_bypass: [null],
      credentials: [''],
      active: [false],
    });
    this.rolesForm = this.formBuilder.group({
      roles: this.formBuilder.array([]),
      note: ['', Validators.required],
    });
    this.licenseForm = this.formBuilder.group({
      selectedState: ['', Validators.required],
      licenseNumber: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.getAllRoles();
    this.getAllStates();
    this.getUser();
    this.isUserValidForSignatureUpload$ = this.user$.pipe(map(() => this.allowSignatureUpload()));
  }

  allowSignatureUpload(): boolean {
    const validRoles = [ROLES.DOCTOR.NAME, ROLES.SUPER.NAME, ROLES.SUPERVISING_DOCTOR.NAME, ROLES.STAFF_MANAGER.NAME];
    return this.user.roles
      ? this.user.roles.some(role => validRoles.some(validRole => validRole.toLowerCase() === role.toLowerCase()))
      : false;
  }

  getUser() {
    this.user$ = this.staffUserApiService.getStaffUser(this.userId()).pipe(
      takeUntil(this.unsubscribe$),
      tap((data: any) => {
        data.roles = Object.values(data.roles);
        this.user = data as StaffUser;
        this.dataSource = data.licenses;
        this.populateForm(data);
      }),
      map(data => {
        return JSON.parse(JSON.stringify(data)) as StaffUser;
      }),
      catchError(err => {
        this.user = defaultStaffUser;
        console.log(err);
        return EMPTY;
      }),
    );
  }

  populateForm(data: any) {
    this.userForm.patchValue({
      title: data.title,
      firstname: data.firstname,
      lastname: data.lastname,
      mfa_bypass: data.mfa_bypass,
      credentials: data.credentials,
      active: data.active,
    });
  }

  getAllRoles() {
    this.roles$ = this.Business.getAllRoles().pipe(
      tap(data => {
        Object.values(data).map(role => {
          const rolesArray: FormArray = this.rolesForm.get('roles') as FormArray;
          if (this.user.roles.includes(role) && !rolesArray.value.includes(role)) {
            rolesArray.push(new FormControl(role));
          }
        });
      }),
      map(data =>
        Object.values(data)
          // System role should no longer be assigned to a user BOD-2615
          .filter(role => role.toLowerCase() !== ROLES.SYSTEM.NAME)
          .map(role => ({
            name: role,
            checked: this.user.roles.includes(role),
          })),
      ),
      catchError(err => {
        console.log(err);
        return EMPTY;
      }),
    );
  }

  getAllStates() {
    this.states$ = this.Business.getAllStates().pipe(
      map((data: any) => {
        return [...this.states, ...data];
      }),
      catchError(err => {
        console.log(err);
        return EMPTY;
      }),
    );
  }

  updateRoles(event: any) {
    const rolesArray: FormArray = this.rolesForm.get('roles') as FormArray;
    this.Utils.updateFormArray(event, rolesArray);
  }

  updateUserRoles() {
    const formValues = this.rolesForm.value;
    this.rolesForm.markAllAsTouched();
    if (this.rolesForm.invalid) {
      this.snackBar.openErrorMessage(this.Notifications.users.invalidForm);
      return;
    }
    if (!formValues.note) {
      this.snackBar.openErrorMessage(this.Notifications.users.noteMissing);
      return;
    } else {
      this.staffUserApiService
        .updateStaffUser(this.user.id, formValues)
        .pipe(
          takeUntil(this.unsubscribe$),
          map(() => {
            this.snackBar.openSuccessMessage(this.Notifications.users.updateSuccess);
          }),
          catchError(err => {
            console.log(err);
            this.snackBar.openErrorMessage(this.Notifications.generic.failure);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  updateUserDetails() {
    const formValues = this.userForm.value;
    if (formValues.mfa_bypass === null) {
      delete formValues.mfa_bypass;
    }
    const payload = this.Utils.findDifference(this.user, formValues);
    this.userForm.markAllAsTouched();

    if (_.isEmpty(payload)) {
      return;
    }
    if (this.userForm.invalid) {
      this.snackBar.openErrorMessage(this.Notifications.users.invalidForm);
      return;
    }
    this.staffUserApiService
      .updateStaffUser(this.user.id, payload)
      .pipe(
        map(() => {
          this.snackBar.openSuccessMessage(this.Notifications.users.updateSuccess);
          this.getUser();
        }),
        catchError(err => {
          console.log(err);
          this.snackBar.openErrorMessage(this.Notifications.generic.failure);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  addLicense() {
    const formValues = this.licenseForm.value;
    this.licenseForm.markAllAsTouched();

    if (this.licenseForm.invalid) {
      this.snackBar.openErrorMessage(this.Notifications.users.invalidForm);
      return;
    }

    if (formValues.selectedState === 'SELECT') {
      this.snackBar.openErrorMessage('Please select a state.');
      return;
    }

    if (!formValues.licenseNumber) {
      this.snackBar.openErrorMessage('Please enter a license number.');
      return;
    }

    this.staffUserApiService
      .addStaffUserLicense(this.user.id, formValues.selectedState, formValues.licenseNumber)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.snackBar.openSuccessMessage('License added successfully.');
          this.licenseForm.reset();
          this.getUser();
        }),
        catchError(err => {
          console.log(err);
          this.snackBar.openErrorMessage(`Error adding license ${err.state} - ${err.message}`);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  removeLicense(stateCode: any) {
    this.staffUserApiService
      .removeStaffUser(this.user.id, stateCode)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.snackBar.openSuccessMessage('License removed successfully.');
          this.getUser();
        }),
        catchError(err => {
          this.snackBar.openErrorMessage(`Error removing license ${err.status} - ${err.message}`);
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
